import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import LicenseExtendMenuItem from "@/v2-backoffice-client/license/extend/LicenseExtendMenuItem"
import LicenseConvertMenuItem from "@/v2-backoffice-client/license/convert/LicenseConvertMenuItem"
import LicenseEndMenuItem from "@/v2-backoffice-client/license/end/LicenseEndMenuItem"
import LicenseSeatsMenuItem from "@/v2-backoffice-client/license/seats/LicenseSeatsMenuItem"
import LicenseSeatHoldersMenuItem from "@/v2-backoffice-client/license/seats/LicenseSeatHoldersMenuItem"
import LicenseStartDateMenuItem from "@/v2-backoffice-client/license/startDate/LicenseStartDateMenuItem"
import LicenseDeleteMenuItem from "@/v2-backoffice-client/license/delete/LicenseDeleteMenuItem"
import LicenseHistoryMenuItem from "@/v2-backoffice-client/license/history/LicenseHistoryMenuItem"
import LicenseChangePlanMenuItem from "@/v2-backoffice-client/license/update/LicenseChangePlanMenuItem"
import { isLicenseEditable } from "@/v2-backoffice-client/license/license.utils"
import {
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseTableRowToolbarProps = {
  license: LicenseFragment
}

// Row actions by state. A Fenerum-derived license shows no actions at all:
// billing owns it, and any edit here would be overwritten on the next sync —
// the API enforces the same rule, this just keeps the dead ends out of the
// menu.
function LicenseTableRowToolbar(props: LicenseTableRowToolbarProps) {
  const { license } = props

  if (!isLicenseEditable(license)) {
    return (
      <ActionMenu>
        <ActionMenu.Trigger />
        <ActionMenu.Content>
          {/* Not "read-only": the label explains why the lifecycle actions
              (extend/end/delete) are absent, while the items below stay —
              the seat-cap OVERRIDE, the seat limit and its names are
              app-owned and survive every sync run, history is a view.
              Add-ons are the customer's, managed in the Add-ons section
              under the table. */}
          <ActionMenu.Label>Managed by billing (Fenerum)</ActionMenu.Label>
          <ActionMenu.Separator />
          {license.SeatsAreAssigned ? (
            <LicenseSeatHoldersMenuItem license={license} />
          ) : (
            <LicenseSeatsMenuItem license={license} />
          )}
          <LicenseHistoryMenuItem license={license} />
        </ActionMenu.Content>
      </ActionMenu>
    )
  }

  const isTrial = license.LicenseStatusCode === LICENSE_STATUS_TRIAL
  const isExpired = license.LicenseStatusCode === LICENSE_STATUS_EXPIRED
  // An ended trial keeps its date, so it stays extendable — moving the end
  // date forward is the first half of reviving an expired trial (the status
  // flip back to Trial is deliberately not automated here yet).
  const isExtendable = isTrial || (isExpired && !!license.TrialEndsAt)

  return (
    <ActionMenu>
      <ActionMenu.Trigger />

      <ActionMenu.Content>
        {(isTrial || isExtendable) && (
          <>
            <LicenseExtendMenuItem license={license} />
            {isTrial && <LicenseConvertMenuItem license={license} />}
            <ActionMenu.Separator />
          </>
        )}

        {/* Seated products (the MCP servers): the Seats dialog holds the
            mode (everyone, or named people), the names and the count in
            one place. For every other product the cap is a reported number
            and there is nobody to name, so it is the cap alone. */}
        {license.SeatsAreAssigned ? (
          <LicenseSeatHoldersMenuItem license={license} />
        ) : (
          <LicenseSeatsMenuItem license={license} />
        )}
        <LicenseChangePlanMenuItem license={license} />
        <LicenseStartDateMenuItem license={license} />

        <ActionMenu.Separator />
        <LicenseHistoryMenuItem license={license} />

        {/* The two ways a license stops: ending it (its users lose what it
            carries, the row stays as the record) and deleting it (for
            mistakes). Together at the foot, both in red. */}
        <ActionMenu.Separator />
        {!isExpired && <LicenseEndMenuItem license={license} />}
        <LicenseDeleteMenuItem license={license} />
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicenseTableRowToolbar
